import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { observer } from 'mobx-react-lite';
import { useSnackbar } from 'notistack';

import { FileStatus } from '@libs/constants';
import { Trans } from '@libs/i18n';
import { FileUploadType } from '@libs/types';
import { getFileExtension, validateUploadFile } from '@libs/utils';

import {
  useConfirmUpload,
  useListProfileDocuments,
  useRequestUploadUrl,
} from '../api/tenant-profile-documents-api';

/**
 * Accepted prerequisite document types — shared by the dropzone and browse
 * input. Documents only; images (png/jpg/jpeg) are intentionally excluded from
 * the hub upload.
 */
export const TENANT_PROFILE_ALLOWED_FILE_TYPES = [
  '.pdf',
  '.doc',
  '.docx',
  '.xlsx',
  '.xls',
] as const;
export const TENANT_PROFILE_MAX_FILE_SIZE_MB = 10;
const MAX_CONCURRENT_PENDING = 30;

/** A pending upload row, tagged with its confirmed document id once known. */
export type PendingUpload = FileUploadType & { docId?: string };

/** Per-file bookkeeping kept outside React state (backing File + in-flight XHR). */
interface FileMeta {
  file: File;
  docId?: string;
  xhr?: XMLHttpRequest;
}

interface TenantProfileUploadContextValue {
  pendingUploads: PendingUpload[];
  addFiles: (files: File[]) => void;
  cancelUpload: (id: string) => void;
  retryUpload: (id: string) => void;
  removeFailed: (id: string) => void;
}

const TenantProfileUploadContext =
  createContext<TenantProfileUploadContextValue | null>(null);

/** S3 PUT with upload progress, abortable via the returned XHR. */
function putWithProgress(
  url: string,
  file: File,
  contentType: string,
  onProgress: (pct: number) => void,
  onStart: (xhr: XMLHttpRequest) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    onStart(xhr);
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable) onProgress(Math.round((ev.loaded / ev.total) * 100));
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`S3 PUT failed with status ${xhr.status}`));
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.onabort = () => reject(new DOMException('Upload cancelled', 'AbortError'));
    xhr.send(file);
  });
}

/**
 * Owns the per-file upload state machine for the tenant profile (request URL →
 * S3 PUT → confirm) so it can be driven from anywhere on the page — the
 * whole-page dropzone and the "Upload files" browse button both call `addFiles`.
 * Confirmation auto-classifies and (when valid) auto-extracts server-side, so
 * there is no extract step here. Once a confirmed upload shows up in the polled
 * documents list, its pending row is dropped — the server row takes over in the
 * InFlight bar / hub table.
 */
export const TenantProfileUploadProvider: FC<{ children: ReactNode }> = observer(
  ({ children }) => {
    const { enqueueSnackbar } = useSnackbar();
    const requestUrl = useRequestUploadUrl();
    const confirm = useConfirmUpload();
    const { data: allDocs = [] } = useListProfileDocuments();

    const [pendingUploads, setPendingUploads] = useState<PendingUpload[]>([]);
    const metaRef = useRef<Map<string, FileMeta>>(new Map());
    const seqRef = useRef(0);

    const serverDocIds = useMemo(
      () => new Set(allDocs.map((d) => d.id)),
      [allDocs],
    );

    // Once a confirmed upload appears in the server list, drop its pending row
    // and backing meta — the server row is now the single representation.
    useEffect(() => {
      setPendingUploads((prev) => {
        const next = prev.filter((p) => {
          const settled = p.docId != null && serverDocIds.has(p.docId);
          if (settled) metaRef.current.delete(p.id);
          return !settled;
        });
        return next.length === prev.length ? prev : next;
      });
    }, [serverDocIds]);

    const patch = useCallback((id: string, partial: Partial<PendingUpload>) => {
      setPendingUploads((prev) =>
        prev.map((f) => (f.id === id ? { ...f, ...partial } : f)),
      );
    }, []);

    const removeFile = useCallback((id: string) => {
      metaRef.current.delete(id);
      setPendingUploads((prev) => prev.filter((f) => f.id !== id));
    }, []);

    const runUpload = useCallback(
      async (id: string) => {
        const meta = metaRef.current.get(id);
        if (!meta) return;
        const { file } = meta;
        const contentType = file.type || 'application/octet-stream';
        patch(id, {
          status: FileStatus.UPLOADING,
          progress: 0,
          errorMessage: undefined,
        });
        try {
          const { uploadUrl, documentId, s3Key } = await requestUrl.mutateAsync({
            fileName: file.name,
            contentType,
          });
          await putWithProgress(
            uploadUrl,
            file,
            contentType,
            (pct) => patch(id, { progress: pct }),
            (xhr) => {
              meta.xhr = xhr;
            },
          );
          const res = await confirm.mutateAsync({
            documentId,
            s3Key,
            fileName: file.name,
            contentType,
            fileSize: file.size,
          });
          meta.docId = res.tenantProfileDocumentId;
          meta.xhr = undefined;
          // Tag the pending row with its document id; it stays visible until the
          // list refetch includes it, then the server row takes over (effect).
          patch(id, {
            status: FileStatus.UPLOADED,
            progress: 100,
            s3Key,
            docId: res.tenantProfileDocumentId,
          });
        } catch (err) {
          if (err instanceof DOMException && err.name === 'AbortError') return;
          patch(id, {
            status: FileStatus.FAILED,
            errorMessage: file.name,
          });
        }
      },
      [confirm, patch, requestUrl],
    );

    const reportValidationError = useCallback(
      (
        type: 'size' | 'type' | 'maxFiles',
        fileName: string,
        fileExtension?: string,
      ) => {
        if (type === 'size') {
          enqueueSnackbar(
            <Trans
              i18nKey="fileUpload.errors.fileSizeExceeded"
              values={{ fileName, maxSize: TENANT_PROFILE_MAX_FILE_SIZE_MB }}
            />,
            { variant: 'error' },
          );
        } else if (type === 'type') {
          enqueueSnackbar(
            <Trans
              i18nKey="fileUpload.errors.fileTypeNotAllowed"
              values={{
                fileExtension,
                allowedTypes: TENANT_PROFILE_ALLOWED_FILE_TYPES.join(', ').toUpperCase(),
              }}
            />,
            { variant: 'error' },
          );
        } else {
          enqueueSnackbar(
            <Trans
              i18nKey="fileUpload.errors.maxFilesExceeded"
              values={{ maxFiles: MAX_CONCURRENT_PENDING }}
            />,
            { variant: 'error' },
          );
        }
      },
      [enqueueSnackbar],
    );

    const addFiles = useCallback(
      (incoming: File[]) => {
        // Count only uploads still occupying a slot (queued/uploading/just-confirmed
        // but not yet absorbed by the server list); failed rows don't block.
        let activeCount = pendingUploads.filter(
          (p) => p.status !== FileStatus.FAILED,
        ).length;
        const valid: File[] = [];

        for (const file of incoming) {
          const { valid: ok, error } = validateUploadFile(file, {
            allowedFileTypes: TENANT_PROFILE_ALLOWED_FILE_TYPES,
            maxFileSizeMb: TENANT_PROFILE_MAX_FILE_SIZE_MB,
            maxFiles: MAX_CONCURRENT_PENDING,
            currentFileCount: activeCount,
          });
          if (ok) {
            valid.push(file);
            activeCount += 1;
            continue;
          }
          // The slot cap is global to this batch — once hit, stop processing.
          if (error === 'maxFiles') {
            reportValidationError('maxFiles', file.name);
            break;
          }
          reportValidationError(error === 'size' ? 'size' : 'type', file.name, getFileExtension(file.name));
        }

        if (valid.length === 0) return;

        const added: PendingUpload[] = valid.map((file) => {
          const id = `tp-upload-${seqRef.current++}`;
          metaRef.current.set(id, { file });
          return {
            id,
            fileName: file.name,
            size: file.size,
            mimeType: file.type,
            uploadedAt: '',
            status: FileStatus.QUEUED,
            progress: 0,
          };
        });
        setPendingUploads((prev) => [...prev, ...added]);
        added.forEach((f) => void runUpload(f.id));
      },
      [pendingUploads, reportValidationError, runUpload],
    );

    const cancelUpload = useCallback(
      (id: string) => {
        metaRef.current.get(id)?.xhr?.abort();
        removeFile(id);
      },
      [removeFile],
    );

    const retryUpload = useCallback((id: string) => void runUpload(id), [runUpload]);

    const value = useMemo<TenantProfileUploadContextValue>(
      () => ({
        pendingUploads,
        addFiles,
        cancelUpload,
        retryUpload,
        removeFailed: removeFile,
      }),
      [pendingUploads, addFiles, cancelUpload, retryUpload, removeFile],
    );

    return (
      <TenantProfileUploadContext.Provider value={value}>
        {children}
      </TenantProfileUploadContext.Provider>
    );
  },
);

export function useTenantProfileUpload(): TenantProfileUploadContextValue {
  const ctx = useContext(TenantProfileUploadContext);
  if (!ctx) {
    throw new Error(
      'useTenantProfileUpload must be used within a TenantProfileUploadProvider',
    );
  }
  return ctx;
}
